import { useState, useEffect, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getCareerById } from 'services/career.service'
import { CareerDetailsInfo } from 'interfaces/CareerInterface'

type UserInteractionData = {
  is_previewed: boolean
  is_deep_dive: boolean
  time_spent: number
  last_visited: string
  page_visits: number
}

interface CareerContext {
  isOnCareerPage: boolean
  careerId: string | null
  careerData: CareerDetailsInfo | null
  isLoading: boolean
  error: Error | null
  userInteractionData: UserInteractionData | null
  updateInteractionData: (updates: Partial<UserInteractionData>) => void
}

// Career page route patterns
const careerPagePatterns = [
  /\/careers\/career-insights\/([^/]+)/,
  /\/partner-careers\/careers\/career-insights\/([^/]+)/,
  /\/user-careers\/careers\/career-insights\/([^/]+)/,
  /\/user-careers\/deeper-dive\/([^/]+)/,
]

const useCareerContext = (): CareerContext => {
  const location = useLocation()
  const params = useParams()
  const [isOnCareerPage, setIsOnCareerPage] = useState(false)
  const [careerId, setCareerId] = useState<string | null>(null)
  const [isDeeperDive, setIsDeeperDive] = useState(false)
  const [userInteractionData, setUserInteractionData] =
    useState<UserInteractionData | null>(null)

  // Track time spent on career pages
  const startTimeRef = useRef<number>(Date.now())
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const pathname = location.pathname
    let foundCareerId: string | null = null
    let isCareerPage = false

    // Check if current path matches any career page pattern
    for (const pattern of careerPagePatterns) {
      const match = pathname.match(pattern)
      if (match) {
        foundCareerId = match[1]
        isCareerPage = true
        break
      }
    }

    // Also check URL params for career ID
    if (!foundCareerId && params.param) {
      // If we have a param and we're on a career-related page, use it
      const isCareerRelatedPage =
        pathname.includes('career-insights') || pathname.includes('deeper-dive')
      if (isCareerRelatedPage) {
        foundCareerId = params.param
        isCareerPage = true
      }
    }

    setIsOnCareerPage(isCareerPage)
    setCareerId(foundCareerId)
    setIsDeeperDive(isCareerPage && pathname.includes('deeper-dive'))

    // Initialize or update interaction data when career changes
    if (foundCareerId && isCareerPage) {
      const storageKey = `career_interaction_${foundCareerId}`
      const existingData = localStorage.getItem(storageKey)

      let interactionData: UserInteractionData

      if (existingData) {
        interactionData = JSON.parse(existingData)
        interactionData.page_visits += 1
        interactionData.last_visited = new Date().toISOString()
      } else {
        interactionData = {
          is_previewed: true,
          is_deep_dive: pathname.includes('deeper-dive'),
          time_spent: 0,
          last_visited: new Date().toISOString(),
          page_visits: 1,
        }
      }

      // Update deep dive status if on deep dive page
      if (pathname.includes('deeper-dive')) {
        interactionData.is_deep_dive = true
      }

      setUserInteractionData(interactionData)
      localStorage.setItem(storageKey, JSON.stringify(interactionData))

      // Start tracking time
      startTimeRef.current = Date.now()

      // Update time spent every 30 seconds
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }

      intervalRef.current = setInterval(() => {
        const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000)
        const updatedData = {
          ...interactionData,
          time_spent: interactionData.time_spent + timeSpent,
        }
        setUserInteractionData(updatedData)
        localStorage.setItem(storageKey, JSON.stringify(updatedData))
        startTimeRef.current = Date.now()
      }, 30000)
    } else {
      // Clear interaction data when not on career page
      setUserInteractionData(null)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [location.pathname, params.param])

  // Fetch career data when we have a career ID
  // For deeper-dive pages, use the deeper-dive API (careerId is unlock ID), otherwise use getCareerById (careerId is career ID)
  const {
    data: rawCareerData,
    isLoading,
    error,
  } = useQuery({
    queryKey: isDeeperDive
      ? ['GetCareerForDeeperDive', careerId, 1] // Match the deeper-dive query key
      : ['career-context', careerId],
    queryFn: async () => {
      if (isDeeperDive) {
        // Import the deeper-dive service
        const { deeperDiveLevelChanged } = await import(
          'services/deeper-dive.service'
        )
        return deeperDiveLevelChanged(careerId!, 1)
      } else {
        return getCareerById(careerId!)
      }
    },
    enabled: !!careerId && isOnCareerPage,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  // Transform deeper-dive data to match CareerDetailsInfo format
  const careerData =
    isDeeperDive && rawCareerData
      ? ({
          id: (rawCareerData as any).career.id,
          name: (rawCareerData as any).career.name,
          description: (rawCareerData as any).career.description,
          superpower: (rawCareerData as any).career.superpower,
          icon_url: (rawCareerData as any).career.icon_url,
          // Add other required fields with defaults
          expert_voices: [],
          education_paths: [],
          skill_requirements: [],
        } as any)
      : rawCareerData

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  const updateInteractionData = (updates: Partial<UserInteractionData>) => {
    if (careerId && userInteractionData) {
      const updatedData = { ...userInteractionData, ...updates }
      setUserInteractionData(updatedData)

      const storageKey = `career_interaction_${careerId}`
      localStorage.setItem(storageKey, JSON.stringify(updatedData))
    }
  }

  return {
    isOnCareerPage,
    careerId,
    careerData: careerData || null,
    isLoading,
    error: error as Error | null,
    userInteractionData,
    updateInteractionData,
  }
}

export default useCareerContext
